import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, Lock, Minus, ChevronDown, ChevronUp, Shield, X, FileImage, Film, File } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────── Login ─────────────────────── */

function LoginScreen({ onLogin, error }) {
  const [pw, setPw] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div style={ls.wrap}>
      <div style={ls.card}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🌲</div>
        <h1 style={ls.title}>Magic Forest</h1>
        <p style={ls.sub}>Storage Migration</p>
        <form onSubmit={e => { e.preventDefault(); onLogin(pw); }} style={{ width: '100%', marginTop: 24 }}>
          <input ref={inputRef} type="password" placeholder="Admin password"
            value={pw} onChange={e => setPw(e.target.value)} style={ls.input} />
          {error && <p style={ls.error}>{error}</p>}
          <button type="submit" style={ls.btn}>Sign In</button>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────── Step Definitions ─────────────────────── */

const STEPS = [
  {
    id: 'discover', number: 1,
    title: 'Discover Legacy Assets',
    desc: 'Scan persistent disk for files not yet registered in the database.',
    why: 'We need to know what exists on disk before we can copy anything to S3.',
    endpoint: 'assets/discover', method: 'POST',
    statusFromInventory: (inv) => {
      if (!inv) return 'unknown';
      const df = inv.disk_files || 0;
      const accounted = (inv.legacy_total || 0) + (inv.disk_already_on_s3 || 0);
      // No disk files → nothing to discover
      if (df === 0) return 'not_needed';
      // All disk files are accounted for (registered as legacy OR linked to existing S3)
      if (accounted >= df) return 'complete';
      // Some registered but not all → partial progress
      if (accounted > 0) return 'partial';
      // Disk files exist but nothing discovered yet → ready to run
      return 'ready';
    },
    context: (inv) => {
      const ctx = [
        { label: 'Disk files', value: inv?.disk_files ?? '—' },
        { label: 'Disk size', value: inv?.disk_size ?? '—' },
        { label: 'Registered', value: inv?.legacy_total ?? 0 },
      ];
      if (inv?.disk_already_on_s3 > 0) {
        ctx.push({ label: 'Already on S3', value: inv.disk_already_on_s3, color: '#4ade80' });
      }
      return ctx;
    },
  },
  {
    id: 'copy', number: 2,
    title: 'Copy to S3',
    desc: 'Upload each discovered legacy file from disk to Hetzner S3 object storage.',
    why: 'Files must exist in S3 before we can verify integrity and switch routing.',
    endpoint: 'assets/copy-all', method: 'POST',
    statusFromInventory: (inv) => {
      if (!inv) return 'unknown';
      if (inv.legacy_total === 0) return 'awaiting_discovery';
      if (inv.states?.pending > 0) return 'ready';
      if ((inv.states?.copied > 0 || inv.states?.verified > 0) && !inv.states?.pending) return 'complete';
      // All legacy assets already past copy stage
      if (inv.legacy_total > 0 && !inv.states?.pending) return 'complete';
      return 'locked';
    },
    context: (inv) => [
      { label: 'Pending copy', value: inv?.states?.pending || 0 },
      { label: 'Copied', value: inv?.states?.copied || 0 },
      { label: 'Failed', value: inv?.states?.failed || 0, color: '#f87171' },
    ],
  },
  {
    id: 'verify', number: 3,
    title: 'Verify Integrity',
    desc: 'Compare each S3 copy against the original on disk (size check).',
    why: 'Must confirm byte-identical copies before switching traffic away from disk.',
    endpoint: 'assets/verify-all', method: 'POST',
    statusFromInventory: (inv) => {
      if (!inv) return 'unknown';
      if (inv.legacy_total === 0) return 'awaiting_discovery';
      if (inv.states?.copied > 0) return 'ready';
      if (inv.states?.verified > 0 && !inv.states?.copied) return 'complete';
      // No copied, no verified — nothing to verify yet
      return 'locked';
    },
    context: (inv) => [
      { label: 'To verify', value: inv?.states?.copied || 0 },
      { label: 'Verified', value: inv?.states?.verified || 0 },
    ],
  },
  {
    id: 'switch', number: 4,
    title: 'Switch to S3',
    desc: 'Update each asset\'s storage_backend to "s3" in the database.',
    why: 'This is the actual cutover — traffic switches from disk to S3.',
    endpoint: 'assets/switch-all', method: 'POST',
    statusFromInventory: (inv) => {
      if (!inv) return 'unknown';
      if (inv.legacy_total === 0) return 'awaiting_discovery';
      if (inv.states?.verified > 0 && inv.legacy_on_disk > 0) return 'ready';
      if (inv.legacy_on_disk === 0 && inv.legacy_total > 0) return 'complete';
      return 'locked';
    },
    context: (inv) => [
      { label: 'Legacy on disk', value: inv?.legacy_on_disk ?? 0 },
      { label: 'Verified', value: inv?.states?.verified || 0 },
      { label: 'On S3', value: inv?.s3_count ?? 0, color: '#4ade80' },
    ],
  },
  {
    id: 'rewrite', number: 5,
    title: 'Rewrite Carousel URLs',
    desc: 'Replace legacy "/api/uploads/..." URLs in carousel JSONB with bgAssetId.',
    why: 'Old carousels may still contain hardcoded disk URLs that bypass S3.',
    endpoint: 'carousel-urls/rewrite-all', method: 'POST',
    needsPreview: true,
    statusFromInventory: (inv) => {
      if (!inv) return 'unknown';
      if (inv.legacy_urls === 0) return 'not_needed';
      if (inv.legacy_urls > 0) return 'ready';
      return 'locked';
    },
    context: (inv) => [
      { label: 'Affected carousels', value: inv?.affected_carousels ?? 0 },
      { label: 'Legacy URLs', value: inv?.legacy_urls ?? 0 },
    ],
  },
  {
    id: 'cleanup', number: 6,
    title: 'Cleanup Disk Files',
    desc: 'Delete migrated legacy files from persistent disk. This only deletes the local disk copy — not S3 objects or asset records.',
    why: 'Once S3 is confirmed working, we reclaim disk space. Asset metadata is preserved.',
    endpoint: 'cleanup/execute', method: 'POST', body: { confirmation: 'DELETE DISK FILES' },
    dangerous: true,
    statusFromInventory: (inv) => {
      if (!inv) return 'unknown';
      const dr = inv._dryRun?.summary;
      if (!dr) return 'unknown';
      // Nothing deletable AND no disk files → not needed
      if (inv._dryRun?.total_disk_files === 0) return 'not_needed';
      if (dr.safe_to_delete > 0) return 'ready';
      // Disk files exist but nothing safe to delete
      if (dr.total_blocked > 0 || dr.investigate > 0 || dr.no_db_match > 0) return 'locked';
      return 'not_needed';
    },
    context: (inv) => {
      const dr = inv?._dryRun?.summary;
      return [
        { label: 'Safe to delete', value: dr?.safe_to_delete ?? 0, color: dr?.safe_to_delete > 0 ? '#4ade80' : undefined },
        { label: 'Blocked', value: dr?.total_blocked ?? 0, color: dr?.total_blocked > 0 ? '#f87171' : undefined },
        { label: 'Investigate', value: dr?.investigate ?? 0, color: dr?.investigate > 0 ? '#fbbf24' : undefined },
        { label: 'Space to free', value: dr?.deletable_bytes_human ?? '0 B' },
      ];
    },
  },
];

/* ─────────────────────── Workflow Progress Widget ─────────────────────── */

function WorkflowProgress({ steps, getStatus, inventory }) {
  // Determine overall phase
  const statuses = steps.map((s, i) => getStatus(s, i));
  const completedCount = statuses.filter(s => s === 'complete' || s === 'not_needed').length;
  const currentIdx = statuses.findIndex(s => s !== 'complete' && s !== 'not_needed');
  const totalSteps = steps.length;

  // Determine overall state message
  let phaseLabel, phaseColor, phaseDesc;
  if (completedCount === totalSteps) {
    phaseLabel = 'MIGRATION COMPLETE';
    phaseColor = '#4ade80';
    phaseDesc = 'All assets on S3. No legacy files or URLs remain.';
  } else if (currentIdx === -1) {
    phaseLabel = 'MIGRATION COMPLETE';
    phaseColor = '#4ade80';
    phaseDesc = 'All steps finished.';
  } else if (currentIdx === 0 && statuses[0] === 'ready') {
    phaseLabel = 'PRE-MIGRATION';
    phaseColor = '#60a5fa';
    phaseDesc = `${inventory?.disk_files || 0} disk files detected. Run Step 1 to begin.`;
  } else {
    const currentStep = steps[currentIdx];
    phaseLabel = `STEP ${currentStep.number} OF ${totalSteps}`;
    phaseColor = '#fbbf24';
    phaseDesc = currentStep.title;
  }

  const progressPct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  return (
    <div style={wp.container}>
      {/* Phase indicator */}
      <div style={wp.phaseRow}>
        <div>
          <div style={{ ...wp.phaseLabel, color: phaseColor }}>{phaseLabel}</div>
          <div style={wp.phaseDesc}>{phaseDesc}</div>
        </div>
        <div style={wp.progressCircle}>
          <svg width="48" height="48" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="20" fill="none" stroke="#1a1a1a" strokeWidth="3" />
            <circle cx="24" cy="24" r="20" fill="none" stroke={phaseColor} strokeWidth="3"
              strokeDasharray={`${progressPct * 1.256} 125.6`}
              strokeLinecap="round" transform="rotate(-90 24 24)"
              style={{ transition: 'stroke-dasharray 0.5s ease' }} />
          </svg>
          <span style={{ ...wp.progressText, color: phaseColor }}>{progressPct}%</span>
        </div>
      </div>

      {/* Step dots */}
      <div style={wp.dotsRow}>
        {steps.map((step, idx) => {
          const st = statuses[idx];
          const isCurrent = idx === currentIdx;
          const done = st === 'complete' || st === 'not_needed';
          const skipped = st === 'not_needed';

          let dotColor = '#1f1f1f';
          let dotBorder = '#1f1f1f';
          let labelColor = '#2a2a2a';
          if (done) { dotColor = '#0f3d0f'; dotBorder = '#2d5a2d'; labelColor = '#4ade80'; }
          else if (isCurrent) { dotColor = '#1a2a0a'; dotBorder = '#4ade80'; labelColor = '#e5e5e5'; }

          return (
            <React.Fragment key={step.id}>
              {idx > 0 && (
                <div style={{
                  flex: 1, height: 2, maxWidth: 60,
                  backgroundColor: done ? '#1a3a1a' : '#1a1a1a',
                  alignSelf: 'center', marginTop: -12,
                }} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 64 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: dotColor, border: `2px solid ${dotBorder}`,
                  fontSize: 11, fontWeight: 700, transition: 'all 0.3s',
                  color: done ? '#4ade80' : isCurrent ? '#4ade80' : '#3a3a3a',
                  boxShadow: isCurrent ? `0 0 12px ${phaseColor}20` : 'none',
                }}>
                  {done ? (skipped ? '—' : '✓') : step.number}
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 600, color: labelColor,
                  textAlign: 'center', lineHeight: 1.2, maxWidth: 72,
                  letterSpacing: isCurrent ? '0.02em' : '0',
                }}>
                  {step.title.replace('Legacy ', '').replace('Carousel ', '').replace(' Files', '')}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const wp = {
  container: {
    padding: '18px 22px', borderRadius: 10,
    backgroundColor: '#0c0c0c', border: '1px solid #1a1a1a',
    marginBottom: 16,
  },
  phaseRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 18,
  },
  phaseLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
    fontFamily: 'monospace',
  },
  phaseDesc: {
    fontSize: 13, color: '#737373', marginTop: 3,
  },
  progressCircle: {
    position: 'relative', width: 48, height: 48,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  progressText: {
    position: 'absolute', fontSize: 11, fontWeight: 700,
    fontFamily: 'monospace',
  },
  dotsRow: {
    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
    gap: 0, padding: '0 10px',
  },
};

/* ─────────────────────── Confirmation Dialogs ─────────────────────── */

function ConfirmModal({ title, message, warning, onConfirm, onCancel, confirmLabel = 'Confirm', isDangerous = false }) {
  return (
    <div style={modal.overlay}>
      <div style={modal.box}>
        <h3 style={modal.title}>{title}</h3>
        <p style={modal.message}>{message}</p>
        {warning && (
          <div style={modal.warningBox}>
            <AlertTriangle size={14} color="#fbbf24" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{warning}</span>
          </div>
        )}
        <div style={modal.actions}>
          <button onClick={onCancel} style={modal.cancelBtn}>Cancel</button>
          <button onClick={onConfirm} style={isDangerous ? modal.dangerConfirmBtn : modal.confirmBtn}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function TypeToConfirmModal({ title, message, warning, confirmWord, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('');
  const canConfirm = typed === confirmWord;
  return (
    <div style={modal.overlay}>
      <div style={modal.box}>
        <h3 style={modal.title}>{title}</h3>
        <p style={modal.message}>{message}</p>
        {warning && (
          <div style={modal.warningBox}>
            <AlertTriangle size={14} color="#f87171" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{warning}</span>
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <label style={{ fontSize: 11, color: '#525252', display: 'block', marginBottom: 6 }}>
            Type <strong style={{ color: '#f87171', fontFamily: 'monospace' }}>{confirmWord}</strong> to confirm:
          </label>
          <input
            value={typed} onChange={e => setTyped(e.target.value)}
            placeholder={confirmWord}
            style={modal.input}
            autoFocus
          />
        </div>
        <div style={modal.actions}>
          <button onClick={onCancel} style={modal.cancelBtn}>Cancel</button>
          <button onClick={onConfirm} disabled={!canConfirm}
            style={{ ...modal.dangerConfirmBtn, opacity: canConfirm ? 1 : 0.3, cursor: canConfirm ? 'pointer' : 'not-allowed' }}>
            Delete Files Permanently
          </button>
        </div>
      </div>
    </div>
  );
}

const modal = {
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  box: {
    padding: '28px 28px 22px', borderRadius: 12, width: 440, maxWidth: '90vw',
    backgroundColor: '#141414', border: '1px solid #1f1f1f',
  },
  title: { margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#fafafa' },
  message: { margin: '0 0 12px', fontSize: 13, color: '#737373', lineHeight: 1.5 },
  warningBox: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '10px 14px', borderRadius: 6,
    backgroundColor: '#1a1505', border: '1px solid #3a2a0a',
    fontSize: 12, color: '#fbbf24', lineHeight: 1.4,
  },
  input: {
    width: '100%', padding: '10px 14px', borderRadius: 6,
    border: '1px solid #2a1a1a', backgroundColor: '#0a0a0a',
    color: '#f87171', fontSize: 14, fontFamily: 'monospace', fontWeight: 700,
    outline: 'none', boxSizing: 'border-box',
  },
  actions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 },
  cancelBtn: {
    padding: '8px 16px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: 'transparent', color: '#525252', cursor: 'pointer',
    fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif',
  },
  confirmBtn: {
    padding: '8px 16px', borderRadius: 6, border: '1px solid #1a3a1a',
    backgroundColor: '#0a1a0a', color: '#4ade80', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
  },
  dangerConfirmBtn: {
    padding: '8px 16px', borderRadius: 6, border: '1px solid #5a2d2d',
    backgroundColor: '#1a0a0a', color: '#f87171', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
  },
};

/* ─────────────────────── Main ─────────────────────── */

export default function AdminMigration() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_pw') || '');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stepResults, setStepResults] = useState({});
  const [runningStep, setRunningStep] = useState(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [expandedResults, setExpandedResults] = useState({});

  // Modal states
  const [showMaintenanceConfirm, setShowMaintenanceConfirm] = useState(false);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [showRewritePreview, setShowRewritePreview] = useState(false);
  const [rewritePreviewData, setRewritePreviewData] = useState(null);
  const [pendingStep, setPendingStep] = useState(null);

  // Preview panel states — which steps have preview expanded
  const [previewOpen, setPreviewOpen] = useState({});
  const [previewData, setPreviewData] = useState({});

  const headers = { 'X-Admin-Password': password };

  const handleLogin = async (pw) => {
    setAuthError('');
    try {
      const resp = await fetch(`${API}/api/admin/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (resp.status === 401) { setAuthError('Invalid password'); return; }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setPassword(pw); sessionStorage.setItem('admin_pw', pw); setAuthed(true);
    } catch { setAuthError('Connection failed'); }
  };

  useEffect(() => { if (password && !authed) handleLogin(password); }, []); // eslint-disable-line

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const [dbR, urlR, dryR, maintR, diskR] = await Promise.all([
        fetch(`${API}/api/admin/migration/inventory/db`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/migration/inventory/carousel-urls`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/migration/cleanup/dry-run`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/migration/maintenance-mode`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/migration/inventory/disk`, { headers }).then(r => r.json()).catch(() => ({ total_files: 0, files: [] })),
      ]);
      const bd = dbR.breakdown || [];
      const legacy = bd.filter(b => b.storage_backend === 'legacy_local');
      const states = {};
      bd.forEach(b => { states[b.migration_state] = (states[b.migration_state] || 0) + b.count; });

      setInventory({
        total_assets: dbR.total_assets, total_size: dbR.total_size_human,
        legacy_total: legacy.reduce((s, r) => s + r.count, 0),
        legacy_on_disk: legacy.reduce((s, r) => s + r.count, 0),
        s3_count: bd.filter(b => b.storage_backend === 's3').reduce((s, r) => s + r.count, 0),
        legacy_urls: urlR.total_legacy_urls || 0, affected_carousels: urlR.total_affected || 0,
        deletable: dryR.summary?.safe_to_delete || 0, deletable_size: dryR.summary?.deletable_bytes_human || '0 B',
        disk_files: diskR.total_files || 0, disk_size: diskR.total_size_human || '0 B',
        disk_already_on_s3: diskR.already_on_s3 || 0,
        states, breakdown: bd,
        // Preserve raw data for previews
        _urlDetail: urlR,
        _diskFiles: diskR.files || [],
        _dryRun: dryR,
      });
      setMaintenanceStatus(maintR);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [password]); // eslint-disable-line

  useEffect(() => { if (authed) loadInventory(); }, [authed, loadInventory]);

  const executeStep = async (step) => {
    setRunningStep(step.id);
    try {
      const opts = { method: step.method, headers: { ...headers, 'Content-Type': 'application/json' } };
      if (step.body) opts.body = JSON.stringify(step.body);
      const resp = await fetch(`${API}/api/admin/migration/${step.endpoint}`, opts);
      if (!resp.ok) {
        const errBody = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}${errBody ? ': ' + errBody : ''}`);
      }
      const result = await resp.json();
      setStepResults(prev => ({ ...prev, [step.id]: { ok: true, ...result } }));
      await loadInventory();
    } catch (e) {
      setStepResults(prev => ({ ...prev, [step.id]: { ok: false, error: e.message } }));
    }
    setRunningStep(null);
  };

  const handleStepAction = (step) => {
    // Dangerous step → typed confirmation
    if (step.id === 'cleanup') {
      setShowCleanupConfirm(true);
      setPendingStep(step);
      return;
    }
    // Rewrite step → preview first
    if (step.id === 'rewrite' && step.needsPreview && !showRewritePreview) {
      setRewritePreviewData(inventory?._urlDetail || null);
      setShowRewritePreview(true);
      setPendingStep(step);
      return;
    }
    executeStep(step);
  };

  const toggleMaintenance = () => {
    setShowMaintenanceConfirm(true);
  };

  const confirmToggleMaintenance = async () => {
    const next = !maintenanceStatus?.enabled;
    setShowMaintenanceConfirm(false);
    const resp = await fetch(`${API}/api/admin/migration/maintenance-mode`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next, reason: 'Media storage migration' }),
    });
    if (resp.ok) setMaintenanceStatus(await resp.json());
  };

  if (!authed) return <LoginScreen onLogin={handleLogin} error={authError} />;

  const getStatus = (step, idx) => {
    const raw = step.statusFromInventory(inventory);
    // "awaiting_discovery" — check if Step 1 already completed:
    // If yes AND legacy_total===0, discovery ran but found nothing to migrate → not_needed
    // If Step 1 hasn't completed, show as locked
    if (raw === 'awaiting_discovery') {
      if (idx > 0) {
        const prevStatus = getStatus(STEPS[0], 0);
        if (prevStatus === 'complete' || prevStatus === 'not_needed') return 'not_needed';
      }
      return 'locked';
    }
    if (raw === 'not_needed' || raw === 'complete') return raw;
    if (raw === 'unknown') return idx === 0 ? 'ready' : 'locked';
    if (idx > 0) {
      const prev = getStatus(STEPS[idx - 1], idx - 1);
      if (prev !== 'complete' && prev !== 'not_needed') return 'locked';
    }
    return raw;
  };

  // "All done" — no legacy assets, no legacy URLs, nothing to delete
  // Disk files are OK if they all have S3 copies (just fallback copies)
  const diskHandled = !inventory?.disk_files || inventory.disk_files === 0 || inventory.disk_already_on_s3 >= inventory.disk_files;
  const allDone = inventory
    && inventory.legacy_total === 0
    && inventory.legacy_urls === 0
    && inventory.deletable === 0
    && diskHandled;

  const toggleResult = (id) => setExpandedResults(prev => ({ ...prev, [id]: !prev[id] }));

  // Lazy-load preview items for a step
  const togglePreview = async (stepId) => {
    const isOpen = previewOpen[stepId];
    if (isOpen) {
      setPreviewOpen(prev => ({ ...prev, [stepId]: false }));
      return;
    }
    // Already loaded?
    if (previewData[stepId]) {
      setPreviewOpen(prev => ({ ...prev, [stepId]: true }));
      return;
    }
    // Load data based on step
    try {
      let items = [];
      if (stepId === 'discover') {
        items = (inventory?._diskFiles || []).map(f => ({
          id: f.path,
          name: f.filename || f.path,
          detail: f.category || '—',
          size: fmtBytes(f.size_bytes),
          diskPath: f.path,
          contentType: f.path.endsWith('.mp4') ? 'video/mp4' : f.path.endsWith('.jpg') ? 'image/jpeg' : null,
        }));
      } else if (stepId === 'copy') {
        const resp = await fetch(`${API}/api/admin/migration/assets?migration_state=pending&limit=200`, { headers });
        const data = await resp.json();
        items = (data.assets || []).map(a => ({
          id: a.asset_id,
          assetId: a.asset_id,
          name: a.filename_original || a.asset_id,
          detail: `${a.kind} · ${a.storage_backend}`,
          size: fmtBytes(a.size_bytes),
          path: a.legacy_path,
          contentType: a.content_type,
          state: a.migration_state,
        }));
      } else if (stepId === 'verify') {
        const resp = await fetch(`${API}/api/admin/migration/assets?migration_state=copied&limit=200`, { headers });
        const data = await resp.json();
        items = (data.assets || []).map(a => ({
          id: a.asset_id,
          assetId: a.asset_id,
          name: a.filename_original || a.asset_id,
          detail: `${a.kind} · copied → to verify`,
          size: fmtBytes(a.size_bytes),
          contentType: a.content_type,
          state: a.migration_state,
        }));
      } else if (stepId === 'switch') {
        const resp = await fetch(`${API}/api/admin/migration/assets?migration_state=verified&limit=200`, { headers });
        const data = await resp.json();
        items = (data.assets || []).map(a => ({
          id: a.asset_id,
          assetId: a.asset_id,
          name: a.filename_original || a.asset_id,
          detail: `${a.kind} · verified → switch to S3`,
          size: fmtBytes(a.size_bytes),
          contentType: a.content_type,
          state: a.migration_state,
        }));
      } else if (stepId === 'cleanup') {
        const dr = inventory?._dryRun;
        const cats = dr?.categories || {};
        // Safe to delete
        const safe = cats.safe_to_delete?.items || [];
        items = safe.map(d => ({
          id: d.asset_id || d.path,
          name: d.path || d.asset_id,
          detail: `S3 confirmed · ${d.kind || 'file'}`,
          size: d.disk_size_human || fmtBytes(d.disk_size_bytes),
          category: 'safe_to_delete',
        }));
        // Blocked categories
        const blockedCats = [
          { key: 'blocked_legacy_url', label: 'Blocked: legacy URL in carousel' },
          { key: 'blocked_no_s3_confirmation', label: 'Blocked: S3 HEAD failed' },
          { key: 'blocked_not_s3_backend', label: 'Blocked: not S3 backend' },
          { key: 'blocked_no_storage_key', label: 'Blocked: no storage_key' },
        ];
        for (const bc of blockedCats) {
          const bItems = cats[bc.key]?.items || [];
          if (bItems.length) {
            items.push({ id: `_header_${bc.key}`, name: `— ${bc.label} (${bItems.length}) —`, detail: '', size: '', isHeader: true });
            bItems.forEach(b => items.push({
              id: (b.asset_id || b.path) + `_${bc.key}`,
              name: b.path || b.asset_id,
              detail: b.reason?.slice(0, 80) || bc.label,
              size: b.disk_size_human || fmtBytes(b.disk_size_bytes),
              blocked: true,
              category: bc.key,
            }));
          }
        }
        // Investigate (filename only)
        const inv_fn = cats.investigate_filename_only?.items || [];
        if (inv_fn.length) {
          items.push({ id: '_header_investigate', name: `— Investigate: filename-only match (${inv_fn.length}) —`, detail: '', size: '', isHeader: true });
          inv_fn.forEach(b => items.push({
            id: (b.matched_asset_id || b.path) + '_investigate',
            name: b.path || b.filename,
            detail: `matched: ${b.matched_asset_id || '?'} (filename only)`,
            size: b.disk_size_human || fmtBytes(b.disk_size_bytes),
            blocked: true,
            category: 'investigate',
          }));
        }
        // No DB match
        const noDb = cats.no_db_match?.items || [];
        if (noDb.length) {
          items.push({ id: '_header_nodb', name: `— No DB match (${noDb.length}) —`, detail: '', size: '', isHeader: true });
          noDb.forEach(b => items.push({
            id: b.path + '_nodb',
            name: b.path || b.filename,
            detail: 'No media_asset record found',
            size: b.disk_size_human || fmtBytes(b.disk_size_bytes),
            blocked: true,
            category: 'no_db_match',
          }));
        }
        // Inconsistent (already cleaned but file exists)
        const incon = cats.inconsistent_already_cleaned_disk_exists?.items || [];
        if (incon.length) {
          items.push({ id: '_header_inconsistent', name: `— Inconsistent: cleaned but exists (${incon.length}) —`, detail: '', size: '', isHeader: true });
          incon.forEach(b => items.push({
            id: (b.asset_id || b.path) + '_inconsistent',
            name: b.path || b.asset_id,
            detail: `Cleaned at: ${b.legacy_disk_deleted_at || '?'} but file still exists`,
            size: b.disk_size_human || fmtBytes(b.disk_size_bytes),
            blocked: true,
            category: 'inconsistent',
          }));
        }
      }
      setPreviewData(prev => ({ ...prev, [stepId]: items }));
      setPreviewOpen(prev => ({ ...prev, [stepId]: true }));
    } catch (e) {
      console.error(`Preview load failed for ${stepId}:`, e);
    }
  };

  return (
    <AdminLayout activePage="migration" onLogout={() => { sessionStorage.removeItem('admin_pw'); setAuthed(false); }}>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#525252' }}>
          Legacy disk → S3 cutover · Step-by-step workflow
        </p>
        <button onClick={loadInventory} disabled={loading} style={p.refreshBtn}>
          <RefreshCw size={13} /> {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* ── Workflow Progress Widget ── */}
      <WorkflowProgress steps={STEPS} getStatus={getStatus} inventory={inventory} />

      {/* ── Summary metrics ── */}
      <div style={p.summaryBar}>
        <Metric label="Total Assets" value={inventory?.total_assets ?? '—'} />
        <Metric label="On S3" value={inventory?.s3_count ?? '—'} color="#4ade80" />
        <Metric label="Legacy (DB)" value={inventory?.legacy_total ?? '0'} color={inventory?.legacy_total > 0 ? '#fbbf24' : '#4ade80'} />
        <Metric label="Legacy URLs" value={inventory?.legacy_urls ?? '0'} color={inventory?.legacy_urls > 0 ? '#fbbf24' : '#4ade80'} />
        <Metric label="Disk Files" value={inventory?.disk_files ?? '—'} sub={inventory?.disk_size} color={inventory?.disk_files > 0 ? '#60a5fa' : '#4ade80'} />
      </div>

      {/* ── Disk files warning ── */}
      {inventory && inventory.disk_files > 0 && inventory.legacy_total === 0 && (
        inventory.disk_already_on_s3 >= inventory.disk_files ? (
          <div style={{ ...p.warningBanner, borderColor: '#1a3a1a', backgroundColor: '#050f05', color: '#4ade80' }}>
            <CheckCircle size={14} color="#4ade80" style={{ flexShrink: 0 }} />
            <div>
              <strong>{inventory.disk_files} disk files</strong> — all already have S3 copies. These are local fallback copies that can be safely deleted after migration Step 6.
            </div>
          </div>
        ) : (
          <div style={p.warningBanner}>
            <AlertTriangle size={14} color="#60a5fa" style={{ flexShrink: 0 }} />
            <div>
              <strong>{inventory.disk_files} files</strong> found on persistent disk ({inventory.disk_size}) but not yet registered in the database.
              {inventory.disk_already_on_s3 > 0 && <> ({inventory.disk_already_on_s3} already on S3.)</>}
              {' '}Run <strong>Step 1 — Discover</strong> to begin the migration.
            </div>
          </div>
        )
      )}

      {/* ── Maintenance Mode ── */}
      <div style={{
        ...p.maintBar,
        borderColor: maintenanceStatus?.enabled ? '#3a2a0a' : '#1a1a1a',
        backgroundColor: maintenanceStatus?.enabled ? '#0f0d05' : '#0f0f0f',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={14} color={maintenanceStatus?.enabled ? '#fbbf24' : '#3a3a3a'} />
          <div>
            <span style={{ fontSize: 12, fontWeight: 600, color: maintenanceStatus?.enabled ? '#fbbf24' : '#d4d4d4' }}>
              Maintenance Mode {maintenanceStatus?.enabled ? 'ACTIVE' : 'OFF'}
            </span>
            {maintenanceStatus?.enabled && (
              <div style={{ fontSize: 10, color: '#8a7530', marginTop: 2 }}>
                Media uploads and exports are blocked (503)
              </div>
            )}
          </div>
        </div>
        <button onClick={toggleMaintenance} style={{
          ...p.smallBtn,
          color: maintenanceStatus?.enabled ? '#4ade80' : '#fbbf24',
          borderColor: maintenanceStatus?.enabled ? '#1a3a1a' : '#3a2a0a',
        }}>
          {maintenanceStatus?.enabled ? 'Disable' : 'Enable'}
        </button>
      </div>

      {/* ── Steps ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {STEPS.map((step, idx) => {
          const status = getStatus(step, idx);
          const running = runningStep === step.id;
          const result = stepResults[step.id];
          const locked = status === 'locked';
          const done = status === 'complete';
          const skip = status === 'not_needed';
          const isCurrent = !done && !skip && !locked;
          const isExpanded = expandedResults[step.id];

          const statusConfig = {
            locked:     { color: '#2a2a2a', label: 'Waiting',    icon: Lock },
            ready:      { color: '#4ade80', label: 'Ready',      icon: null },
            partial:    { color: '#fbbf24', label: 'Partial',    icon: null },
            complete:   { color: '#4ade80', label: 'Done',       icon: CheckCircle },
            not_needed: { color: '#3a3a3a', label: 'Not needed', icon: Minus },
            unknown:    { color: '#3a3a3a', label: '...',        icon: null },
          };
          const sc = statusConfig[status] || statusConfig.unknown;
          const StatusIcon = sc.icon;

          return (
            <div key={step.id} style={{ position: 'relative' }}>
              {/* Connector line */}
              {idx < STEPS.length - 1 && (
                <div style={{
                  position: 'absolute', left: 35, top: '100%', width: 2, height: 10,
                  backgroundColor: done || skip ? '#1a3a1a' : '#1a1a1a', zIndex: 1,
                }} />
              )}

              <div style={{
                display: 'flex', gap: 16, padding: '18px 20px', borderRadius: 8,
                backgroundColor: isCurrent ? '#0a0f0a' : '#0f0f0f',
                border: `1px solid ${isCurrent ? '#1a3a1a' : step.dangerous && isCurrent ? '#3a1a1a' : '#1a1a1a'}`,
                marginBottom: 10, opacity: locked ? 0.35 : 1,
                transition: 'all 0.2s',
              }}>
                {/* Number circle */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                  border: `2px solid ${done ? '#2d5a2d' : isCurrent ? '#2d5a2d' : '#1f1f1f'}`,
                  backgroundColor: done ? '#0a2a0a' : '#111',
                  color: done ? '#4ade80' : isCurrent ? '#4ade80' : '#3a3a3a',
                }}>
                  {done ? '✓' : skip ? '—' : step.number}
                </div>

                {/* Body */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Title + status badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: locked ? '#3a3a3a' : '#e5e5e5' }}>
                      {step.title}
                    </h3>
                    <span style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                      backgroundColor: `${sc.color}12`, color: sc.color, border: `1px solid ${sc.color}25`,
                    }}>
                      {StatusIcon && <StatusIcon size={10} />}
                      {sc.label}
                    </span>
                  </div>

                  <p style={{ margin: '0 0 6px', fontSize: 12, color: '#525252', lineHeight: 1.5 }}>{step.desc}</p>

                  {/* Why this step */}
                  {isCurrent && (
                    <div style={{
                      padding: '6px 10px', borderRadius: 4,
                      backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a', marginBottom: 8,
                    }}>
                      <span style={{ fontSize: 10, color: '#525252' }}>Why: </span>
                      <span style={{ fontSize: 10, color: '#737373' }}>{step.why}</span>
                    </div>
                  )}

                  {/* Context metrics */}
                  {isCurrent && step.context && (
                    <div style={{ display: 'flex', gap: 24, marginBottom: 10 }}>
                      {step.context(inventory).map((m, i) => (
                        <div key={i}>
                          <div style={{ fontSize: 9, color: '#3a3a3a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: m.color || '#d4d4d4', fontFamily: 'monospace' }}>{m.value}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Preview panel — show actual items before action */}
                  {isCurrent && step.id !== 'rewrite' && (
                    <div style={{ marginBottom: 10 }}>
                      <button
                        onClick={() => togglePreview(step.id)}
                        style={p.previewToggle}
                      >
                        {previewOpen[step.id]
                          ? <><ChevronUp size={12} /> Hide items</>
                          : <><ChevronDown size={12} /> Show items</>
                        }
                      </button>
                      {previewOpen[step.id] && previewData[step.id] && (
                        <ItemPreviewTable items={previewData[step.id]} stepId={step.id} password={password} />
                      )}
                    </div>
                  )}

                  {/* Action button */}
                  {isCurrent && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button onClick={() => handleStepAction(step)} disabled={running} style={{
                        ...(step.dangerous ? p.dangerBtn : p.actionBtn),
                        opacity: running ? 0.5 : 1,
                      }}>
                        {running
                          ? '⏳ Running...'
                          : step.dangerous
                            ? `⚠ ${step.title}`
                            : step.needsPreview
                              ? `🔍 Preview & ${step.title}`
                              : `▶ ${step.title}`
                        }
                      </button>
                    </div>
                  )}

                  {/* Result */}
                  {result && (
                    <div style={{
                      marginTop: 10, borderRadius: 6,
                      backgroundColor: result.ok ? '#0a140a' : '#140a0a',
                      border: `1px solid ${result.ok ? '#1a3a1a' : '#3a1a1a'}`,
                      overflow: 'hidden',
                    }}>
                      <div
                        onClick={() => toggleResult(step.id)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 12px', cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontSize: 12, color: result.ok ? '#4ade80' : '#f87171' }}>
                          {result.ok ? '✅ ' : '❌ '}{fmtResult(step, result)}
                        </span>
                        {(result.details || result.failures || result.error) && (
                          isExpanded
                            ? <ChevronUp size={14} color="#3a3a3a" />
                            : <ChevronDown size={14} color="#3a3a3a" />
                        )}
                      </div>
                      {isExpanded && (result.details || result.failures || result.error) && (
                        <div style={{
                          padding: '8px 12px', borderTop: `1px solid ${result.ok ? '#1a3a1a' : '#3a1a1a'}`,
                          fontSize: 11, color: '#737373', fontFamily: 'monospace',
                          maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap',
                        }}>
                          {result.error && <div style={{ color: '#f87171' }}>{result.error}</div>}
                          {result.failures && result.failures.length > 0 && (
                            <div>
                              <div style={{ color: '#f87171', marginBottom: 4 }}>Failed items:</div>
                              {result.failures.map((f, i) => (
                                <div key={i} style={{ marginLeft: 8, marginBottom: 2 }}>
                                  • {f.filename || f.asset_id || f.id}: {f.error || 'unknown error'}
                                </div>
                              ))}
                            </div>
                          )}
                          {result.details && <div>{typeof result.details === 'string' ? result.details : JSON.stringify(result.details, null, 2)}</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Completion ── */}
      {allDone && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px',
          borderRadius: 8, marginTop: 10, backgroundColor: '#0a140a', border: '1px solid #1a3a1a',
        }}>
          <CheckCircle size={24} color="#4ade80" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#4ade80' }}>Migration Complete</div>
            <div style={{ fontSize: 11, color: '#3a5a3a', marginTop: 2 }}>
              All assets on S3. No legacy files, URLs, or disk dependencies remain.
              You can now safely remove the persistent disk from Render.com.
            </div>
          </div>
        </div>
      )}

      {/* ── Link to Storage Health page ── */}
      <div style={{ marginTop: 24, borderTop: '1px solid #1a1a1a', paddingTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e5e5e5' }}>
              S3 ↔ DB Reconciliation
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#525252' }}>
              Compare S3 objects against database records, validate orphans, and safely delete unused files
            </p>
          </div>
          <a href="/admin/storage" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 16px', borderRadius: 6, border: '1px solid #2d5a2d',
            backgroundColor: '#0a1a0a', color: '#4ade80', textDecoration: 'none',
            fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
          }}>
            Open Storage Health →
          </a>
        </div>
      </div>

      {/* ── Modals ── */}

      {/* Maintenance mode confirmation */}
      {showMaintenanceConfirm && (
        <ConfirmModal
          title={maintenanceStatus?.enabled ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
          message={maintenanceStatus?.enabled
            ? 'This will re-enable media uploads, exports, and background changes for all users.'
            : 'This will block all media uploads, exports, and background changes. Users will see 503 errors on media operations.'
          }
          warning={!maintenanceStatus?.enabled
            ? 'All active users will be unable to upload backgrounds, export slides, or modify media until you disable maintenance mode.'
            : null
          }
          confirmLabel={maintenanceStatus?.enabled ? 'Disable Maintenance' : 'Enable Maintenance'}
          isDangerous={!maintenanceStatus?.enabled}
          onConfirm={confirmToggleMaintenance}
          onCancel={() => setShowMaintenanceConfirm(false)}
        />
      )}

      {/* Cleanup typed confirmation */}
      {showCleanupConfirm && (
        <TypeToConfirmModal
          title="Delete Legacy Disk Files"
          message={`This will permanently delete ${inventory?._dryRun?.summary?.safe_to_delete || 0} disk file(s) (${inventory?._dryRun?.summary?.deletable_bytes_human || '0 B'}) from the persistent disk. Only files classified as safe_to_delete will be removed.`}
          warning="This only deletes local disk copies. S3 objects, asset records, and lifecycle metadata are untouched. Each file is revalidated (S3 HEAD + carousel URL check) immediately before deletion."
          confirmWord="DELETE DISK FILES"
          onConfirm={() => {
            setShowCleanupConfirm(false);
            if (pendingStep) executeStep(pendingStep);
            setPendingStep(null);
          }}
          onCancel={() => { setShowCleanupConfirm(false); setPendingStep(null); }}
        />
      )}

      {/* Rewrite preview */}
      {showRewritePreview && (
        <div style={modal.overlay}>
          <div style={{ ...modal.box, width: 520 }}>
            <h3 style={modal.title}>Preview: Carousel URL Rewrite</h3>
            <p style={modal.message}>
              This will scan all carousel JSONB data and replace legacy <code style={{ color: '#60a5fa', fontSize: 11 }}>/api/uploads/...</code> URLs
              with <code style={{ color: '#4ade80', fontSize: 11 }}>bgAssetId</code> references.
            </p>

            <div style={{
              padding: '12px 16px', borderRadius: 6, marginBottom: 16,
              backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
            }}>
              <div style={{ display: 'flex', gap: 32, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 9, color: '#3a3a3a', textTransform: 'uppercase' }}>Carousels affected</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fbbf24', fontFamily: 'monospace' }}>
                    {inventory?.affected_carousels ?? 0}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#3a3a3a', textTransform: 'uppercase' }}>Legacy URLs to rewrite</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fbbf24', fontFamily: 'monospace' }}>
                    {inventory?.legacy_urls ?? 0}
                  </div>
                </div>
              </div>
              {rewritePreviewData?.sample_urls && rewritePreviewData.sample_urls.length > 0 && (
                <div>
                  <div style={{ fontSize: 9, color: '#3a3a3a', textTransform: 'uppercase', marginBottom: 4 }}>Sample URLs being rewritten</div>
                  {rewritePreviewData.sample_urls.slice(0, 5).map((url, i) => (
                    <div key={i} style={{ fontSize: 10, color: '#737373', fontFamily: 'monospace', padding: '2px 0' }}>
                      {url}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {inventory?.legacy_urls > 0 && (
              <div style={{ ...modal.warningBox, marginBottom: 16 }}>
                <AlertTriangle size={14} color="#fbbf24" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>This modifies carousel data in the database. A Postgres backup is recommended before proceeding.</span>
              </div>
            )}

            <div style={modal.actions}>
              <button onClick={() => { setShowRewritePreview(false); setPendingStep(null); }} style={modal.cancelBtn}>Cancel</button>
              {inventory?.legacy_urls > 0 ? (
                <button onClick={() => {
                  setShowRewritePreview(false);
                  if (pendingStep) executeStep(pendingStep);
                  setPendingStep(null);
                }} style={modal.confirmBtn}>
                  ▶ Execute Rewrite
                </button>
              ) : (
                <button disabled style={{ ...modal.confirmBtn, opacity: 0.3, cursor: 'not-allowed' }}>
                  Nothing to rewrite
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

/* ─────────────────────── Helpers ─────────────────────── */

/* ─── File Detail Modal ─── */

function FileDetailModal({ item, stepId, password, onClose }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(item.name);
  const isVideo = /\.(mp4|webm|mov|avi|mkv)$/i.test(item.name);
  const canPreview = isImage || isVideo;

  useEffect(() => {
    if (!canPreview) { setLoading(false); return; }
    let cancelled = false;

    (async () => {
      try {
        let url;
        if (stepId === 'discover') {
          // Disk files — use the public legacy route (no auth needed, handles S3 redirect)
          const diskPath = item.diskPath || item.id || '';
          url = `${API}/api/uploads/${diskPath}`;
        } else if (item.assetId) {
          // DB assets — fetch presigned URL via admin endpoint
          const resp = await fetch(`${API}/api/admin/migration/preview/${encodeURIComponent(item.assetId)}`, {
            headers: { 'X-Admin-Password': password },
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const data = await resp.json();
          url = data.url;
        }
        if (!cancelled && url) setPreviewUrl(url);
      } catch (e) {
        if (!cancelled) setError('Could not load preview');
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [item, stepId, password, canPreview]);

  return (
    <div style={fd.overlay} onClick={onClose}>
      <div style={fd.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={fd.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isImage ? <FileImage size={16} color="#60a5fa" /> :
             isVideo ? <Film size={16} color="#a78bfa" /> :
             <File size={16} color="#525252" />}
            <span style={fd.title}>{item.name}</span>
          </div>
          <button onClick={onClose} style={fd.closeBtn}><X size={16} /></button>
        </div>

        {/* Preview area */}
        <div style={fd.previewArea}>
          {loading ? (
            <div style={fd.placeholder}>Loading preview...</div>
          ) : canPreview && previewUrl ? (
            isImage ? (
              <img
                src={previewUrl}
                alt={item.name}
                style={fd.image}
                onError={() => setError('Failed to load image')}
              />
            ) : isVideo ? (
              <video
                src={previewUrl}
                controls
                style={fd.video}
                onError={() => setError('Failed to load video')}
              />
            ) : null
          ) : (
            <div style={fd.placeholder}>
              {error || 'No preview available for this file type'}
            </div>
          )}
          {error && <div style={fd.errorMsg}>{error}</div>}
        </div>

        {/* Metadata */}
        <div style={fd.metaGrid}>
          <MetaRow label="Filename" value={item.name} />
          {item.path && <MetaRow label="Path" value={item.path} />}
          {item.diskPath && <MetaRow label="Disk path" value={item.diskPath} />}
          {item.assetId && <MetaRow label="Asset ID" value={item.assetId} mono />}
          <MetaRow label="Size" value={item.size} />
          <MetaRow label="Detail" value={item.detail} />
          {item.contentType && <MetaRow label="Type" value={item.contentType} />}
          {item.state && <MetaRow label="State" value={item.state} />}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '4px 0' }}>
      <span style={{ fontSize: 10, color: '#3a3a3a', minWidth: 72, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      <span style={{ fontSize: 11, color: '#a3a3a3', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

const fd = {
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    width: 560, maxWidth: '92vw', maxHeight: '85vh',
    borderRadius: 12, overflow: 'hidden',
    backgroundColor: '#111', border: '1px solid #1f1f1f',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px', borderBottom: '1px solid #1a1a1a',
    backgroundColor: '#0f0f0f',
  },
  title: {
    fontSize: 13, fontWeight: 600, color: '#e5e5e5',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: 400,
  },
  closeBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 6,
    border: '1px solid #1f1f1f', backgroundColor: 'transparent',
    color: '#525252', cursor: 'pointer',
  },
  previewArea: {
    padding: 16, display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: 200, maxHeight: 360, backgroundColor: '#0a0a0a',
    borderBottom: '1px solid #1a1a1a', overflow: 'hidden',
  },
  image: {
    maxWidth: '100%', maxHeight: 340, objectFit: 'contain',
    borderRadius: 4,
  },
  video: {
    maxWidth: '100%', maxHeight: 340, borderRadius: 4,
    backgroundColor: '#000',
  },
  placeholder: {
    fontSize: 12, color: '#3a3a3a', fontStyle: 'italic',
  },
  errorMsg: {
    fontSize: 11, color: '#f87171', marginTop: 8, textAlign: 'center',
  },
  metaGrid: {
    padding: '12px 18px', overflow: 'auto',
  },
};

/* ─── Item Preview Table ─── */

function ItemPreviewTable({ items, stepId, password }) {
  const [selectedItem, setSelectedItem] = useState(null);

  if (!items || items.length === 0) {
    return (
      <div style={pt.empty}>No items found</div>
    );
  }
  return (
    <>
      <div style={pt.wrap}>
        <div style={pt.header}>
          <span style={pt.hCell}>File / Asset</span>
          <span style={{ ...pt.hCell, width: 160 }}>Detail</span>
          <span style={{ ...pt.hCell, width: 72, textAlign: 'right' }}>Size</span>
        </div>
        <div style={pt.body}>
          {items.slice(0, 50).map((item) => (
            item.isHeader ? (
              <div key={item.id} style={pt.headerRow}>{item.name}</div>
            ) : (
              <div
                key={item.id}
                onClick={() => !item.isHeader && setSelectedItem(item)}
                style={{
                  ...pt.row,
                  opacity: item.blocked ? 0.5 : 1,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#141414'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={pt.nameCell} title={item.path || item.name}>
                  <span style={pt.fileIcon}>
                    {/\.(jpg|jpeg|png|webp|gif|svg)$/i.test(item.name) ? '🖼' :
                     /\.(mp4|webm|mov)$/i.test(item.name) ? '🎬' : '📄'}
                  </span>
                  {item.name}
                </span>
                <span style={{ ...pt.detailCell, width: 160 }}>{item.detail}</span>
                <span style={{ ...pt.sizeCell, width: 72 }}>{item.size}</span>
              </div>
            )
          ))}
          {items.length > 50 && (
            <div style={pt.more}>... and {items.length - 50} more items</div>
          )}
        </div>
        <div style={pt.hint}>Click a row to preview the file</div>
      </div>

      {/* Detail modal */}
      {selectedItem && (
        <FileDetailModal
          item={selectedItem}
          stepId={stepId}
          password={password}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </>
  );
}

const pt = {
  wrap: {
    borderRadius: 6, overflow: 'hidden', marginTop: 8,
    border: '1px solid #1a1a1a', backgroundColor: '#0a0a0a',
  },
  empty: {
    padding: '12px 14px', fontSize: 11, color: '#3a3a3a',
    backgroundColor: '#0a0a0a', borderRadius: 6, border: '1px solid #1a1a1a',
    marginTop: 8,
  },
  header: {
    display: 'flex', gap: 8, padding: '6px 12px',
    borderBottom: '1px solid #1a1a1a', backgroundColor: '#0f0f0f',
  },
  hCell: {
    flex: 1, fontSize: 9, color: '#3a3a3a', textTransform: 'uppercase',
    letterSpacing: '0.05em', fontWeight: 600,
  },
  body: {
    maxHeight: 220, overflowY: 'auto',
  },
  row: {
    display: 'flex', gap: 8, padding: '5px 12px',
    borderBottom: '1px solid #111',
  },
  headerRow: {
    padding: '8px 12px', fontSize: 10, color: '#525252', fontWeight: 600,
    borderBottom: '1px solid #1a1a1a', backgroundColor: '#0c0c0c',
  },
  nameCell: {
    flex: 1, fontSize: 11, color: '#a3a3a3', fontFamily: 'monospace',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  detailCell: {
    fontSize: 10, color: '#525252',
  },
  sizeCell: {
    fontSize: 10, color: '#737373', fontFamily: 'monospace', textAlign: 'right',
  },
  more: {
    padding: '6px 12px', fontSize: 10, color: '#3a3a3a', fontStyle: 'italic',
  },
  hint: {
    padding: '5px 12px', fontSize: 9, color: '#2a2a2a', borderTop: '1px solid #111',
    textAlign: 'center', letterSpacing: '0.03em',
  },
  fileIcon: {
    marginRight: 6, fontSize: 11,
  },
};

function Metric({ label, value, color, sub }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: '#3a3a3a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: color || '#e5e5e5', fontFamily: 'monospace' }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: '#3a3a3a' }}>{sub}</div>}
    </div>
  );
}

function fmtBytes(b) {
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let u = 0;
  let v = b;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v.toFixed(u > 0 ? 1 : 0)} ${units[u]}`;
}

function fmtResult(step, r) {
  if (!r.ok) return r.error?.split('\n')[0]?.slice(0, 80) || 'Failed';
  switch (step.id) {
    case 'discover': {
      const total = (r.registered || 0) + (r.linked_existing || 0) + (r.skipped || 0);
      const parts = [];
      if (r.registered) parts.push(`${r.registered} new assets registered`);
      if (r.linked_existing) parts.push(`${r.linked_existing} already on S3`);
      if (r.skipped) parts.push(`${r.skipped} already known`);
      if (r.skipped_hidden) parts.push(`${r.skipped_hidden} hidden files ignored`);
      const summary = parts.join(', ');
      if (r.registered > 0) return `${summary} — proceed to Step 2 (Copy)`;
      return summary || `${total} files processed`;
    }
    case 'copy': return `Copied ${r.copied ?? 0}/${r.total ?? 0}${r.failed ? ` (${r.failed} failed)` : ''} — proceed to Step 3 (Verify)`;
    case 'verify': return `Verified ${r.verified ?? 0}/${r.total ?? 0}${r.failed ? ` (${r.failed} failed)` : ''} — proceed to Step 4 (Switch)`;
    case 'switch': return `Switched ${r.switched ?? 0}/${r.total ?? 0} to S3 — check Step 5 (Rewrite URLs)`;
    case 'rewrite': {
      const msg = `Updated ${r.updated_carousels ?? 0} carousel(s), ${r.updated_slides ?? 0} slide(s)`;
      if (r.total_unmatched > 0) return `${msg} — ⚠ ${r.total_unmatched} URL(s) could not be matched`;
      if (r.match_methods) {
        const m = r.match_methods;
        const methods = [];
        if (m.legacy_path) methods.push(`${m.legacy_path} by path`);
        if (m.filename) methods.push(`${m.filename} by filename`);
        if (m.existing_asset_id) methods.push(`${m.existing_asset_id} already had asset ID`);
        if (methods.length) return `${msg} (${methods.join(', ')})`;
      }
      return msg;
    }
    case 'cleanup': {
      const parts = [`Deleted ${r.deleted_count ?? 0} disk file(s), freed ${r.bytes_deleted_human || '0 B'}`];
      if (r.skipped_count > 0) parts.push(`${r.skipped_count} skipped`);
      if (r.failed_count > 0) parts.push(`${r.failed_count} failed`);
      return parts.join(' · ');
    }
    default: return JSON.stringify(r);
  }
}

/* ─────────────────────── Styles ─────────────────────── */

const p = {
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: '#141414', color: '#737373', cursor: 'pointer', fontSize: 12,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  summaryBar: {
    display: 'flex', gap: 32, padding: '14px 18px', borderRadius: 8,
    backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a', marginBottom: 12,
  },
  warningBanner: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '12px 16px', borderRadius: 8, marginBottom: 12,
    backgroundColor: '#0a0d14', border: '1px solid #1a2a3a',
    fontSize: 12, color: '#60a5fa', lineHeight: 1.5,
  },
  maintBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderRadius: 6, marginBottom: 20,
    backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a',
  },
  smallBtn: {
    padding: '5px 12px', borderRadius: 5, border: '1px solid #1f1f1f',
    backgroundColor: 'transparent', cursor: 'pointer', fontSize: 11, fontWeight: 600,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  actionBtn: {
    padding: '7px 16px', borderRadius: 6, border: '1px solid #2d5a2d',
    backgroundColor: '#0a1a0a', color: '#4ade80', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
  },
  dangerBtn: {
    padding: '7px 16px', borderRadius: 6, border: '1px solid #5a2d2d',
    backgroundColor: '#1a0a0a', color: '#f87171', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
  },
  previewToggle: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 4, border: '1px solid #1f1f1f',
    backgroundColor: 'transparent', color: '#525252', cursor: 'pointer',
    fontSize: 10, fontFamily: 'Inter, system-ui, sans-serif',
  },
};

const ls = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a' },
  card: { padding: 40, borderRadius: 12, width: 340, textAlign: 'center', backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' },
  title: { margin: 0, fontSize: 20, fontWeight: 700, color: '#fafafa' },
  sub: { margin: '4px 0 0', fontSize: 12, color: '#525252' },
  input: { width: '100%', padding: '11px 14px', borderRadius: 6, border: '1px solid #1f1f1f', backgroundColor: '#0a0a0a', color: '#e5e5e5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12, fontFamily: 'Inter, system-ui, sans-serif' },
  btn: { width: '100%', padding: '11px 16px', borderRadius: 6, border: 'none', backgroundColor: '#4ade80', color: '#0a0a0a', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  error: { color: '#f87171', fontSize: 12, margin: '8px 0' },
};
